import axios from 'axios';
import { paths } from '../routes/helpers';
import { UNAUTHORIZED } from '../utils/constants';

// Create the Axios instance
const instance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach token if present in localStorage
instance.interceptors.request.use(
  (config) => {
    const token = JSON.parse(localStorage.getItem('token'));
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Detect SPA index.html mistaken for API JSON (wrong baseURL / dev server)
instance.interceptors.response.use(
  (response) => {
    const data = response.data;
    const isHtml =
      (typeof data === 'string' && /^\s*<!doctype html/i.test(data)) ||
      (typeof data === 'string' && /^\s*<html/i.test(data));
    if (isHtml) {
      const misconfigured = new Error(
        'Received the React app HTML instead of API JSON. Set REACT_APP_API_BASE_URL to your oystr-api URL (e.g. http://localhost:4000/api) and restart npm start.'
      );
      misconfigured.code = 'HTML_NOT_JSON';
      return Promise.reject(misconfigured);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === UNAUTHORIZED) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      window.location.href = paths.public.login();
    }
    return Promise.reject(error);
  }
);

/**
 * We'll structure the exported object by resource.
 * Each resource will have the standard CRUD + any extras.
 */
const requests = {
  // ----------------------------
  // Analytics Endpoints (admin)
  // ----------------------------
  analytics: {
    getDashboard: (params) => instance.get('/analytics/dashboard', { params }),
    getContent: (params) => instance.get('/analytics/content', { params }),
    getBookings: (params) => instance.get('/analytics/bookings', { params }),
    getSearch: (params) => instance.get('/analytics/search', { params }),
    getVenueEngagement: (params) => instance.get('/analytics/venue-engagement', { params }),
    getCuisines: (params) => instance.get('/analytics/cuisines', { params }),
    getLocations: (params) => instance.get('/analytics/locations', { params }),
    getUserBehaviour: (params) => instance.get('/analytics/user-behaviour', { params }),
    getTrends: (params) => instance.get('/analytics/trends', { params }),
    getSpikeAlerts: (params) => instance.get('/analytics/spike-alerts', { params }),
  },

  // ----------------------------
  // Auth Endpoints
  // ----------------------------
  auth: {
    register: (data) => instance.post('/register', data),
    verifyEmail: (token) => instance.get(`/verify-email/${token}`),
    login: (data) => instance.post('/admin/login', data),
    login2FA: (data) => instance.post('/login/2fa', data),
    protectedRoute: () => instance.get('/protected'), // GET /protected
    forgotPassword: (data) => instance.post('/forgot-password', data),
    resetPasswordGet: (token) => instance.get(`/reset-password/${token}`),
    resetPasswordPost: (token, data) => instance.post(`/reset-password/${token}`, data),
    setup2FAGet: () => instance.get('/setup-2fa'),   // GET /setup-2fa
    setup2FAPost: (data) => instance.post('/setup-2fa', data), // POST /setup-2fa
  },

  // If your JWT strategy automatically attaches user info, a 2FA "verify" route:
  verify2FA: (data) => instance.post('/setup-2fa', data),

  // Additional loginWith2FA if needed:
  loginWith2FA: (data) => instance.post('/login/2fa', data),

  // ----------------------------
  // Booking Endpoints
  // ----------------------------
  bookings: {
    create: (data) => instance.post('/bookings', data),
    getAll: (params) => instance.get('/bookings', { params }), 
    getBusinessCounts: () => instance.get('/bookings/business-counts'),
    getById: (id) => instance.get(`/bookings/${id}`),
    getByUserId: (userId) => instance.get(`/bookings/user/${userId}`),
    update: (id, data) => instance.put(`/bookings/${id}`, data),
    delete: (id) => instance.delete(`/bookings/${id}`),
  },

  // ----------------------------
  // Bookmark Endpoints
  // ----------------------------
  bookmarks: {
    create: (data) => instance.post('/bookmarks', data),
    getAll: (params) => instance.get('/bookmarks', { params }),
    getByBusinessId: (businessId) => instance.get(`/bookmarks/business/${businessId}`),
    getById: (id) => instance.get(`/bookmarks/${id}`),
    update: (id, data) => instance.put(`/bookmarks/${id}`, data),
    delete: (id) => instance.delete(`/bookmarks/${id}`),
  },

  // ----------------------------
  // Category Endpoints
  // ----------------------------
  categories: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/categories', formData, { headers });
    },
    getAll: (params) => instance.get('/categories', { params }),
    getById: (id) => instance.get(`/categories/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/categories/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    delete: (id) => instance.delete(`/categories/${id}`),
    search: (params) => instance.get('/categories/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/categories/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Geo import (admin)
  // ----------------------------
  geoImports: {
    create: (formData) =>
      instance.post('/geo-imports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [(data) => data],
      }),
    getAll: (params) => instance.get('/geo-imports', { params }),
    getById: (jobId) => instance.get(`/geo-imports/${jobId}`),
    cancel: (jobId) => instance.post(`/geo-imports/${jobId}/cancel`),
  },

  // ----------------------------
  // Venue locations (admin read-only; create via business placeSync)
  // ----------------------------
  locations: {
    getAll: (params) => instance.get('/locations', { params }),
    getById: (id) => instance.get(`/locations/${id}`),
  },

  // ----------------------------
  // Geo hierarchy browse (admin read-only)
  // ----------------------------
  geo: {
    countries: {
      getAll: (params) => instance.get('/geo/countries', { params }),
      getById: (id) => instance.get(`/geo/countries/${id}`),
    },
    regions: {
      getAll: (params) => instance.get('/geo/regions', { params }),
      getById: (id) => instance.get(`/geo/regions/${id}`),
    },
    cityRegions: {
      getAll: (params) => instance.get('/geo/city-regions', { params }),
      getById: (id) => instance.get(`/geo/city-regions/${id}`),
    },
    cities: {
      getAll: (params) => instance.get('/geo/cities', { params }),
      getById: (id) => instance.get(`/geo/cities/${id}`),
    },
    areas: {
      getAll: (params) => instance.get('/geo/areas', { params }),
      getById: (id) => instance.get(`/geo/areas/${id}`),
    },
    neighbourhoods: {
      getAll: (params) => instance.get('/geo/neighbourhoods', { params }),
      getById: (id) => instance.get(`/geo/neighbourhoods/${id}`),
      search: (params) => instance.get('/geo/neighbourhoods/search', { params }),
      resolve: (id) => instance.get(`/geo/neighbourhoods/${id}/resolve`),
    },
  },

  // ----------------------------
  // Post Endpoints
  // ----------------------------
  posts: {
    create: (data, isFormData = false) => {
      const config = {};
      
      if (isFormData) {
        // For multipart/form-data (file uploads)
        config.headers = { 'Content-Type': 'multipart/form-data' };  
        // Important: Set transformRequest to null to prevent axios from
        // trying to transform FormData into something else
        config.transformRequest = [function (data, headers) {
          return data; // Return the FormData as is
        }];
      }
      
      return instance.post('/posts', data, config);
    },
    getAll: (params) => instance.get('/posts', { params }),
    getById: (id) => instance.get(`/posts/${id}`),
    update: (id, data, isFormData = false) => {
      const config = {};
      
      if (isFormData) {
        // For multipart/form-data (file uploads)
        config.headers = { 'Content-Type': 'multipart/form-data' };
      }
      
      return instance.put(`/posts/${id}`, data, config);
    },
    delete: (id) => instance.delete(`/posts/${id}`),
  },

  // ----------------------------
  // Trend Endpoints
  // ----------------------------
  trends: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/trends', formData, { headers });
    },
    getAll: (params) => instance.get('/trends', { params }),
    getById: (id) => instance.get(`/trends/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/trends/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    updateOrder: (trends) => instance.put('/trends/order', trends),
    delete: (id) => instance.delete(`/trends/${id}`),
    search: (params) => instance.get('/trends/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/trends/bulk-status', { ids, status }),
  },

  // ----------------------------
  // User Endpoints
  // ----------------------------
  users: {
    create: async (data, isFormData = false) => {
      const config = {};
      
      if (isFormData) {
        config.headers = { 'Content-Type': 'multipart/form-data' };
        // Prevent axios from transforming FormData
        config.transformRequest = [function (data, headers) {
          return data; // Return the FormData as is
        }];
      }
      
      return instance.post('/users', data, config);
    },
    getAll: (params) => instance.get('/users', { params }),
    getById: (id) => instance.get(`/users/${id}`),
    update: async (id, data, isFormData = false) => {
      const config = {};
      
      if (isFormData) {
        config.headers = { 'Content-Type': 'multipart/form-data' };
        // Prevent axios from transforming FormData
        config.transformRequest = [function (data, headers) {
          return data; // Return the FormData as is
        }];
      }
      
      return instance.put(`/users/${id}`, data, config);
    },
    delete: (id) => instance.delete(`/users/${id}`),
    bulkUpdateStatus: (ids, status) => instance.put('/users/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Amenity Endpoints
  // ----------------------------
  amenities: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/amenities', formData, { headers });
    },
    getAll: (params) => instance.get('/amenities', { params }),
    getById: (id) => instance.get(`/amenities/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/amenities/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    delete: (id) => instance.delete(`/amenities/${id}`),
    search: (params) => instance.get('/amenities/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/amenities/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Bar Preference Endpoints
  // ----------------------------
  barPreferences: {
    create: async (formData) => { 
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/bar-preferences', formData, { headers });
    },
    getAll: (params) => instance.get('/bar-preferences', { params }),
    getById: (id) => instance.get(`/bar-preferences/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/bar-preferences/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    delete: (id) => instance.delete(`/bar-preferences/${id}`),
    updateOrders: (data) => instance.put('/bar-preferences/reorder', { barPreferences: data }),
    search: (params) => instance.get('/bar-preferences/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/bar-preferences/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Business Endpoints
  // ----------------------------
  businesses: {
    create: async (formData) => {
      return await instance.post('/businesses', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [(data) => data]
      });
    },
    getAll: (params) => instance.get('/businesses', { params }),
    getById: (id) => instance.get(`/businesses/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/businesses/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        transformRequest: [(data) => data]
      });
    },
    delete: (id) => instance.delete(`/businesses/${id}`),
    searchByKeyword: (params) => instance.get('/business-keywords-categories/search-by-keyword', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/businesses/bulk-status', { ids, status }),
  },

  businessImports: {
    create: (formData) =>
      instance.post('/business-imports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [(data) => data],
      }),
    export: ({ template = false } = {}) =>
      instance.get('/business-imports/export', {
        params: template ? { template: '1' } : undefined,
        responseType: 'blob',
      }),
    getAll: (params) => instance.get('/business-imports', { params }),
    getById: (jobId) => instance.get(`/business-imports/${jobId}`),
    cancel: (jobId) => instance.post(`/business-imports/${jobId}/cancel`),
    exportAll: async () => {
      const config = { responseType: 'blob' };
      try {
        return await instance.get('/business-imports/export', config);
      } catch (error) {
        if (error.response?.status === 404) {
          return await instance.get('/business-export', config);
        }
        throw error;
      }
    },
  },

  // ----------------------------
  // Cuisine Endpoints
  // ----------------------------
  cuisines: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/cuisines', formData, { headers });
    },
    getAll: (params) => instance.get('/cuisines', { params }),
    getById: (id) => instance.get(`/cuisines/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/cuisines/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    updateOrders: (data) => instance.put('/cuisines/reorder', { cuisines: data }),
    delete: (id) => instance.delete(`/cuisines/${id}`),
    search: (params) => instance.get('/cuisines/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/cuisines/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Dietary Preference Endpoints
  // ----------------------------
  dietaryPreferences: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/dietary-preferences', formData, { headers });
    },
    getAll: (params) => instance.get('/dietary-preferences', { params }),
    getById: (id) => instance.get(`/dietary-preferences/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/dietary-preferences/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    delete: (id) => instance.delete(`/dietary-preferences/${id}`),
    search: (params) => instance.get('/dietary-preferences/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/dietary-preferences/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Item Endpoints
  // ----------------------------
  items: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/items', formData, { headers });
    },
    getAll: (params) => instance.get('/items', { params }),
    getById: (id) => instance.get(`/items/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/items/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    delete: (id) => instance.delete(`/items/${id}`),
    search: (params) => instance.get('/items/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/items/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Occasion Endpoints
  // ----------------------------
  occasions: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/occasions', formData, { headers });
    },
    getAll: (params) => instance.get('/occasions', { params }),
    getById: (id) => instance.get(`/occasions/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/occasions/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    delete: (id) => instance.delete(`/occasions/${id}`),
    search: (params) => instance.get('/occasions/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/occasions/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Rating Endpoints
  // ----------------------------
  ratings: {
    create: (data) => instance.post('/ratings', data),
    getAll: (params) => instance.get('/ratings', { params }),
    getById: (id) => instance.get(`/ratings/${id}`),
    update: (id, data) => instance.put(`/ratings/${id}`, data),
    delete: (id) => instance.delete(`/ratings/${id}`),
  },

  // ----------------------------
  // TikTok Endpoints
  // ----------------------------
  tiktok: {
    searchUsers: (params) => instance.get('/tiktok/users/search', { params }),
    getUserPosts: (userId, params) => instance.get(`/tiktok/users/${userId}/posts`, { params }),
    searchVideos: (params) => instance.get('/tiktok/videos/search', { params }),
    downloadVideo: (data) => instance.post('/tiktok/videos/download', data),
    searchVideoByUrl: (params) => instance.get('/tiktok/videos/url', { params }),
  },

  // ----------------------------
  // Instagram Endpoints
  // ----------------------------
  instagram: {
    searchVideoByUrl: (params) => instance.get('/instagram/videos/url', { params }),
  },

  // ----------------------------
  // Thread Endpoints
  // ----------------------------
  threads: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/threads', formData, { headers });
    },
    getAll: (params) => instance.get('/threads', { params }),
    getById: (id) => instance.get(`/threads/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/threads/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    delete: (id) => instance.delete(`/threads/${id}`),
    search: (params) => instance.get('/threads/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/threads/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Keyword Category Endpoints
  // ----------------------------
  keywordCategories: {
    create: (data) => instance.post('/keyword-categories', data),
    getAll: (params) => instance.get('/keyword-categories', { params }),
    getActive: () => instance.get('/keyword-categories/active'),
    getById: (id) => instance.get(`/keyword-categories/${id}`),
    update: (id, data) => instance.put(`/keyword-categories/${id}`, data),
    delete: (id) => instance.delete(`/keyword-categories/${id}`),
    duplicate: (id) => instance.post(`/keyword-categories/${id}/duplicate`),
    bulkUpdateStatus: (ids, status) => instance.put('/keyword-categories/bulk-status', { ids, status }),
  },

  // ----------------------------
  // Business Keywords Categories Endpoints
  // ----------------------------
  businessKeywordsCategories: {
    create: (data) => instance.post('/business-keywords-categories', data),
    createBulk: (data) => instance.post('/business-keywords-categories/bulk', data),
    getAll: (params) => instance.get('/business-keywords-categories', { params }),
    getActive: () => instance.get('/business-keywords-categories/active'),
    getById: (id) => instance.get(`/business-keywords-categories/${id}`),
    update: (id, data) => instance.put(`/business-keywords-categories/${id}`, data),
    delete: (id) => instance.delete(`/business-keywords-categories/${id}`),
    bulkDelete: (ids) => instance.post('/business-keywords-categories/bulk-delete', { ids }),
    getByBusiness: (businessId) => instance.get(`/business-keywords-categories/business/${businessId}`),
    getByKeywordCategory: (keywordCategoryId) => instance.get(`/business-keywords-categories/keyword-category/${keywordCategoryId}`),
    searchByKeyword: (params) => instance.get('/business-keywords-categories/search-by-keyword', { params }),
    setForBusiness: (businessId, keywordCategoryIds, status = 1) =>
      instance.put(`/business-keywords-categories/business/${businessId}`, { keywordCategoryIds, status }),
    bulkUpdateStatus: (ids, status) => instance.put('/business-keywords-categories/bulk-status', { ids, status }),
  },


  // ----------------------------
  // Activity Endpoints
  // ----------------------------
  activities: {
    create: async (formData) => {
      const headers = { 'Content-Type': 'multipart/form-data' };
      return await instance.post('/activities', formData, { headers });
    },
    getAll: (params) => instance.get('/activities', { params }),
    getById: (id) => instance.get(`/activities/${id}`),
    update: async (id, formData) => {
      return await instance.put(`/activities/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    updateOrders: (data) => instance.put('/activities/reorder', { activities: data }),
    delete: (id) => instance.delete(`/activities/${id}`),
    search: (params) => instance.get('/activities/search', { params }),
    bulkUpdateStatus: (ids, status) => instance.put('/activities/bulk-status', { ids, status }),
  },

};

export default requests;
